import { useMemo } from 'react';
import { BehaviorSubject } from 'rxjs';
import { isEqual } from 'lodash';
import { datadogLogs } from '@datadog/browser-logs';
import { useAuthenticatedSocket, LIVE_UPDATES_UNAVAILABLE } from './useAuthenticatedSocket';
import { addError, clearError } from '../store/Error';

/**
 * @TODO Define feature flags here as they are added
 */
export type FeatureFlags = Record<string, unknown>;
export const defaultFeatureFlags: FeatureFlags = {};

const subject = new BehaviorSubject<FeatureFlags>(defaultFeatureFlags);

export const getFeatureFlagSubject = (): BehaviorSubject<FeatureFlags> => subject;

// Boundary check, not a shape validator -- #908 removed error envelopes from this channel, and the
// old nested-shape check was vacuously true for an Error (non-enumerable message/stack), risking a wipe to {}.
const isFeatureFlagPayload = (payload: unknown): payload is FeatureFlags =>
  typeof payload === 'object' && payload !== null && !Array.isArray(payload) && Object.keys(payload).length > 0;

const setConfigData = (configData: FeatureFlags) => {
  const prevData = subject.getValue();
  if (isEqual(prevData, configData)) return;
  subject.next(configData);
};

export const useFeatureFlags = (): void => {
  const events = useMemo(
    () => ({
      flags: (payload: unknown) => {
        if (!isFeatureFlagPayload(payload)) {
          // AppConfig has no runtime schema validation, so one bad leaf here discards the whole
          // payload and looks exactly like the "flags never update" outage this hook fixes.
          datadogLogs.logger.warn('Discarding a flags payload that failed the boundary check', { payload });
          return;
        }
        setConfigData({ ...defaultFeatureFlags, ...payload });
      }
    }),
    []
  );

  const handleAvailability = (available: boolean) => {
    if (available) clearError('live-updates');
    else addError('live-updates', LIVE_UPDATES_UNAVAILABLE);
  };

  useAuthenticatedSocket(events, handleAvailability);
};
